﻿import React, { useEffect, useRef, useState } from 'react'
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { TicketBroker } from '../../types/quality'
import { qualityService } from '../../services/quality'
import { formatBrokerTicketLabel } from './qualidadeDisplay'

const MIN_QUERY = 4
const DEBOUNCE_MS = 300

interface BrokerComboboxProps {
  /** ID do corretor selecionado (broker_id). */
  value: string | null
  /** Objeto do corretor já conhecido (ex.: ao editar um ticket existente). Usado para exibir o label inicial sem precisar buscar. */
  initialBroker?: { name: string; id_scgp: number | null } | null
  onChange: (id: string | null, broker: TicketBroker | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Busca só por `brokers.name` (ignora ID SCGP numérico). */
  searchNameOnly?: boolean
  /** `scgp` = "123 - Nome"; `name` = só o nome. */
  labelMode?: 'scgp' | 'name'
}

function brokerOptionLabel(
  broker: { name: string; id_scgp: number | null },
  labelMode: 'scgp' | 'name'
): string {
  if (labelMode === 'name') return broker.name || '—'
  return formatBrokerTicketLabel(broker)
}

export const BrokerCombobox: React.FC<BrokerComboboxProps> = ({
  value,
  initialBroker,
  onChange,
  placeholder = 'Digite o ID do SCGP ou Nome para pesquisar',
  disabled = false,
  className = '',
  searchNameOnly = false,
  labelMode = 'scgp',
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<TicketBroker[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Label do corretor actualmente selecionado
  const selectedLabel =
    value && initialBroker ? brokerOptionLabel(initialBroker, labelMode) : ''

  // Executa busca quando query muda
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < MIN_QUERY) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      const data = await qualityService.searchBrokers(query, { nameOnly: searchNameOnly })
      setResults(data)
      setLoading(false)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, searchNameOnly])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setOpen(true)
  }

  const handleSelect = (broker: TicketBroker | null) => {
    onChange(broker?.id ?? null, broker)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null, null)
    setQuery('')
    setResults([])
    setOpen(false)
  }

  const handleFocus = () => {
    setQuery('')
    setOpen(true)
  }

  const handleBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setOpen(false)
        setQuery('')
      }
    }, 150)
  }

  // O input mostra: texto digitado (quando aberto e buscando) OU label do selecionado (quando fechado)
  const inputDisplayValue = open ? query : selectedLabel

  const showDropdown = open && (query.length > 0 || !!value)

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={inputDisplayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={value ? undefined : placeholder}
          autoComplete="off"
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 pr-16 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-1 gap-0.5">
          {/* Spinner de loading */}
          {loading && (
            <svg className="animate-spin h-3.5 w-3.5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          )}
          {/* Botão limpar */}
          {value && !disabled && !loading && (
            <button
              type="button"
              onMouseDown={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Remover corretor"
              tabIndex={-1}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={e => {
              e.preventDefault()
              if (open) setOpen(false)
              else { (e.currentTarget.previousElementSibling as HTMLElement | null)?.focus?.(); setOpen(true) }
            }}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={disabled}
            tabIndex={-1}
          >
            <ChevronUpDownIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showDropdown && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg text-sm focus:outline-none">
          {/* Sempre mostra opção "Nenhum" */}
          <li
            onMouseDown={e => { e.preventDefault(); handleSelect(null) }}
            className="cursor-pointer select-none px-3 py-2 text-gray-500 dark:text-gray-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 border-b border-gray-100 dark:border-gray-700"
          >
            — Nenhum —
          </li>

          {query.length === 0 && value ? (
            // Só tem selecionado, sem query — não mostra mais opções
            null
          ) : query.length < MIN_QUERY ? (
            <li className="px-3 py-2 text-gray-400 dark:text-gray-500 select-none italic">
              Digite ao menos {MIN_QUERY} caracteres para pesquisar...
            </li>
          ) : loading ? (
            <li className="px-3 py-2 text-gray-400 dark:text-gray-500 select-none">
              Buscando...
            </li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-gray-400 dark:text-gray-500 select-none">
              Nenhum corretor encontrado.
            </li>
          ) : (
            results.map(broker => (
              <li
                key={broker.id}
                onMouseDown={e => { e.preventDefault(); handleSelect(broker) }}
                className={`cursor-pointer select-none px-3 py-2 hover:bg-primary-50 dark:hover:bg-primary-900/30 ${
                  broker.id === value
                    ? 'font-medium text-primary-700 dark:text-primary-300 bg-primary-50/60 dark:bg-primary-900/20'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {brokerOptionLabel(broker, labelMode)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
